import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, RotateCcw, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { t, DOCUMENT_TYPE_BG, countryName } from '@/i18n';
import { formatDateTime, formatNumber } from '@/lib/format';
import { useHotkeys } from '@/lib/hotkeys';
import { useDocumentQueue, useReviewDocument } from '@/api/hooks/documents';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { Checkbox, Field, Textarea } from '@/components/ui/Field';
import { EmptyState, ErrorState, LoadingBlock } from '@/components/ui/State';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/Table';
import type { DocumentSummary } from '@/api/types';
import { toast } from '@/stores/toast';
import { EcmrList } from '@/components/ecmr/EcmrList';
import { DocumentEmailPanel } from '@/components/documents/DocumentEmailPanel';

/** Причини, които се повтарят. Свободният текст остава — но рядко трябва. */
const REJECT_REASONS = [
  'Снимката е нечетлива — направете нова на светло.',
  'Липсва подпис и печат в кл. 24 на ЧМР.',
  'Документът е за друга заявка.',
  'Липсва част от документа — заснемете целия лист.',
];

/**
 * Подпис от екрана на телефона (е-ЧМР, получател) не може да се „заснеме
 * наново" — човекът вече си е тръгнал. Затова при връщане на такъв файл
 * нова снимка не се иска по подразбиране.
 */
function isSignatureImage(document: DocumentSummary): boolean {
  return /^(podpis_|echmr_)/.test(document.fileName);
}

/**
 * „ЧМР и документи": три раздела. Първият е опашката за преглед на снимките
 * от шофьорите; вторият — върнатите, които чакат нова снимка; третият —
 * електронните ЧМР с трите подписа.
 */
export function DocumentsPage() {
  const [tab, setTab] = useState<'review' | 'rejected' | 'email' | 'ecmr'>('review');
  return (
    <div className="flex min-h-full flex-col">
      <div className="flex gap-1 border-b border-line px-3 pt-3 sm:px-4">
        {(
          [
            ['review', 'Снимки за преглед'],
            ['rejected', 'Върнати'],
            ['email', 'Изпращане по имейл'],
            ['ecmr', 'Електронни ЧМР'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              '-mb-px rounded-t-lg border border-b-0 px-4 py-2 text-sm',
              tab === key ? 'border-line bg-surface font-medium text-accent' : 'border-transparent text-ink-dim hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'review' ? (
        <DocumentReviewQueue />
      ) : tab === 'rejected' ? (
        <RejectedDocuments />
      ) : tab === 'email' ? (
        <DocumentEmailPanel />
      ) : (
        <div className="p-3 sm:p-4">
          <EcmrList />
        </div>
      )}
    </div>
  );
}

function DocumentReviewQueue() {
  const { data, isLoading, isError, refetch } = useDocumentQueue('pending_review');
  const review = useReviewDocument();
  const [index, setIndex] = useState(0);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [reshoot, setReshoot] = useState(true);

  const items = useMemo(() => data?.items ?? [], [data]);
  const current = items[Math.min(index, Math.max(items.length - 1, 0))];

  useEffect(() => {
    if (index > items.length - 1) setIndex(Math.max(items.length - 1, 0));
  }, [items.length, index]);

  const accept = () => {
    if (!current) return;
    review.mutate(
      { id: current.id, accept: true },
      { onSuccess: () => toast.ok(t.documents.accepted, current.fileName) },
    );
  };

  const reject = () => {
    if (!current) return;
    if (!reason.trim()) {
      toast.warn(t.documents.rejectRequired);
      return;
    }
    const askDriver = reshoot && current.driverId !== null;
    review.mutate(
      { id: current.id, accept: false, reason: reason.trim(), reshoot: askDriver },
      {
        onSuccess: () => {
          toast.ok(askDriver ? t.documents.rejectedReshoot : t.documents.rejected, current.fileName);
          setRejecting(false);
          setReason('');
        },
      },
    );
  };

  /** „Върни" отваря прозореца с отметката по подразбиране според вида на файла. */
  const openReject = () => {
    if (!current) return;
    setReshoot(current.driverId !== null && !isSignatureImage(current));
    setRejecting(true);
  };

  useHotkeys([
    { keys: 'a', description: t.documents.accept, group: 'Документи', run: accept },
    {
      keys: 'r',
      description: t.documents.reject,
      group: 'Документи',
      run: openReject,
    },
    {
      keys: 'j',
      description: 'Следващ документ',
      group: 'Документи',
      run: () => setIndex((i) => Math.min(i + 1, items.length - 1)),
    },
    {
      keys: 'k',
      description: 'Предишен документ',
      group: 'Документи',
      run: () => setIndex((i) => Math.max(i - 1, 0)),
    },
  ]);

  if (isLoading) return <LoadingBlock />;
  if (isError) return <ErrorState onRetry={() => void refetch()} />;

  return (
    <div className="flex min-h-full flex-col gap-3 p-3 sm:p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-lg font-bold tracking-tight">{t.documents.title}</h1>
        <span className="text-xs text-ink-dim">{items.length} за преглед</span>
        <span className="ml-auto hidden font-mono text-[11px] text-ink-faint lg:inline">
          {t.documents.keyboardHint}
        </span>
      </div>

      {items.length === 0 ? (
        <Card>
          <EmptyState title={t.documents.emptyQueue} />
        </Card>
      ) : (
        <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[18rem_1fr_20rem]">
          {/* На телефон опашката пада най-отдолу: първо се гледа снимката, после се решава. */}
          <Card className="order-3 overflow-hidden lg:order-none">
            <CardHeader title={t.documents.queue} />
            <ul className="max-h-[70vh] overflow-y-auto">
              {items.map((document, i) => (
                <li key={document.id}>
                  <button
                    type="button"
                    onClick={() => setIndex(i)}
                    className={cn(
                      'w-full border-b border-line px-3 py-2 text-left text-sm',
                      i === index ? 'bg-accent-soft text-accent' : 'hover:bg-raised',
                    )}
                  >
                    <div className="truncate font-medium">{DOCUMENT_TYPE_BG[document.type]}</div>
                    <div className="truncate text-[11px] text-ink-faint">
                      {document.orderNumber ?? document.tripNumber ?? '—'} · {document.driverName ?? '—'}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="order-1 flex min-h-[16rem] items-center justify-center overflow-hidden bg-ground p-2 lg:order-none lg:min-h-[24rem]">
            {current?.previewUrl ? (
              current.mimeType.startsWith('image/') ? (
                <img
                  src={current.previewUrl}
                  alt={current.fileName}
                  className="max-h-[45svh] w-auto max-w-full object-contain lg:max-h-[70vh]"
                />
              ) : (
                <iframe
                  title={current.fileName}
                  src={current.previewUrl}
                  className="h-[45svh] w-full rounded border border-line bg-white lg:h-[70vh]"
                />
              )
            ) : (
              <p className="text-sm text-ink-dim">{t.documents.noPreview}</p>
            )}
          </Card>

          <Card className="order-2 flex flex-col lg:order-none">
            <CardHeader
              title={current ? DOCUMENT_TYPE_BG[current.type] : ''}
              subtitle={current?.fileName}
            />
            <CardBody className="flex-1 space-y-3 text-sm">
              {current ? (
                <>
                  <dl className="space-y-1.5 text-xs">
                    <Row label={t.documents.order}>
                      {current.orderId && current.orderNumber ? (
                        <Link
                          to="/orders/$orderId"
                          params={{ orderId: current.orderId }}
                          className="font-mono text-accent hover:underline"
                        >
                          {current.orderNumber}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </Row>
                    {current.tripNumber ? <Row label="Курс">{current.tripNumber}</Row> : null}
                    <Row label="Шофьор">{current.driverName ?? '—'}</Row>
                    {current.notes ? <Row label="Бележка">{current.notes}</Row> : null}
                    {current.driverConfirmedAt ? (
                      <Row label={t.documents.driverConfirmed}>
                        <span className="inline-flex items-center gap-1 text-ok">
                          <Check className="size-3" /> {formatDateTime(current.driverConfirmedAt)}
                        </span>
                      </Row>
                    ) : null}
                    {current.replacesDocumentId ? (
                      <Row label={t.documents.replaces}>
                        <span className="inline-flex items-center gap-1 text-warn">
                          <RotateCcw className="size-3" /> по задача „снимай наново"
                        </span>
                      </Row>
                    ) : null}
                    <Row label={t.documents.capturedAt}>
                      {formatDateTime(current.capturedAt)}
                      {current.capturedCountry ? ` · ${countryName(current.capturedCountry)}` : ''}
                    </Row>
                    <Row label={t.documents.uploadedAt}>{formatDateTime(current.createdAt)}</Row>
                    <Row label="Размер">
                      {formatNumber(Math.round(current.sizeBytes / 1024))} KB
                    </Row>
                    <Row label={t.documents.sha}>
                      <span className="break-all font-mono text-[10px]">{current.sha256}</span>
                    </Row>
                  </dl>

                  {current.ocrData ? (
                    <div className="rounded border border-line bg-raised p-2">
                      <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-wide text-ink-faint">
                        {t.documents.ocr}
                        {current.ocrConfidence != null ? (
                          <Badge tone={current.ocrConfidence > 80 ? 'ok' : 'warn'}>
                            {current.ocrConfidence} %
                          </Badge>
                        ) : null}
                      </div>
                      <ul className="space-y-0.5 text-xs">
                        {Object.entries(current.ocrData).map(([key, value]) => (
                          <li key={key} className="flex justify-between gap-2">
                            <span className="text-ink-faint">{key}</span>
                            <span className="truncate font-mono">{String(value)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </>
              ) : null}
            </CardBody>

            <div className="flex gap-2 border-line p-3 max-lg:order-first max-lg:border-b lg:border-t">
              <Button
                className="flex-1"
                variant="primary"
                icon={<Check className="size-4" />}
                loading={review.isPending}
                onClick={accept}
              >
                {t.documents.accept}
                <kbd className="ml-1 hidden font-mono text-[10px] opacity-70 lg:inline">A</kbd>
              </Button>
              <Button
                className="flex-1"
                variant="danger"
                icon={<X className="size-4" />}
                onClick={openReject}
              >
                {t.documents.reject}
                <kbd className="ml-1 hidden font-mono text-[10px] opacity-70 lg:inline">R</kbd>
              </Button>
            </div>
          </Card>
        </div>
      )}

      <Dialog
        open={rejecting}
        onClose={() => setRejecting(false)}
        title={t.documents.reject}
        description={t.documents.rejectRequired}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRejecting(false)}>
              {t.common.cancel}
            </Button>
            <Button variant="danger" onClick={reject} loading={review.isPending}>
              {t.documents.reject}
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {REJECT_REASONS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setReason(preset)}
                className="rounded border border-line-strong px-2 py-1 text-left text-xs text-ink-dim hover:border-accent/60 hover:text-ink"
              >
                {preset}
              </button>
            ))}
          </div>
          <Field label={t.documents.rejectReason} required>
            <Textarea data-autofocus value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          {current?.driverId ? (
            <div>
              <Checkbox
                label={t.documents.reshoot}
                checked={reshoot}
                onChange={(e) => setReshoot(e.target.checked)}
              />
              <p className="mt-1 text-[11px] text-ink-faint">{t.documents.reshootHint}</p>
            </div>
          ) : null}
        </div>
      </Dialog>
    </div>
  );
}

/**
 * Върнатите документи: кои още чакат нова снимка от шофьора и кои са върнати
 * без такава (хартията е в офиса). Новата снимка влиза в „Снимки за преглед",
 * а върнатата изчезва оттук като заменена.
 */
function RejectedDocuments() {
  const { data, isLoading, isError, refetch } = useDocumentQueue('rejected');
  const items = data?.items ?? [];
  if (isLoading) return <LoadingBlock />;
  if (isError) return <ErrorState onRetry={() => void refetch()} />;
  return (
    <div className="p-3 sm:p-4">
      <Card>
        <CardHeader title="Върнати документи" subtitle={`${items.length} върнати`} />
        {items.length === 0 ? (
          <EmptyState title="Няма върнати документи." />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Документ</TH>
                <TH>Заявка</TH>
                <TH>Шофьор</TH>
                <TH>Причина</TH>
                <TH>Върнат</TH>
                <TH>Състояние</TH>
              </tr>
            </THead>
            <TBody>
              {items.map((document) => (
                <TR key={document.id}>
                  <TD>{DOCUMENT_TYPE_BG[document.type]}</TD>
                  <TD>
                    {document.orderId && document.orderNumber ? (
                      <Link
                        to="/orders/$orderId"
                        params={{ orderId: document.orderId }}
                        className="font-mono text-accent hover:underline"
                      >
                        {document.orderNumber}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TD>
                  <TD>{document.driverName ?? '—'}</TD>
                  <TD className="max-w-[18rem] truncate" title={document.rejectReason ?? ''}>
                    {document.rejectReason ?? '—'}
                  </TD>
                  <TD className="whitespace-nowrap">{formatDateTime(document.reviewedAt)}</TD>
                  <TD>
                    {document.reshootRequestedAt ? (
                      <Badge tone="warn">чака нова снимка</Badge>
                    ) : (
                      <Badge tone="neutral">без нова снимка</Badge>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-ink-faint">{label}</dt>
      <dd className="min-w-0 text-right text-ink">{children}</dd>
    </div>
  );
}
